/**
 * Layout shell (T1, tranche master-plan UI/UX): the 3-zone CSS grid that hosts
 * the map screen — a fixed left layer panel, the map, a dormant right panel,
 * and a full-width bottom zone for the attribute table. Replaces the old
 * `.content` flex container.
 *
 * The shell owns the grid DOM and exposes mount slots; `main.ts` mounts the
 * existing panels into them without changing their internals. The left panel
 * keeps `id="panel"` and the map host keeps `id="map"` so every existing
 * `getElementById()` call in `main.ts` keeps resolving.
 *
 * P0 delivered structure + re-parenting. P1 (this file) adds:
 *  - drag handles resizing the left column and the bottom table row;
 *  - size persistence in localStorage (sizes only, never open/closed state);
 *  - `onResize` fired on every size change (and on table open/close) so the
 *    caller can run `map.updateSize()` and keep OpenLayers un-stretched.
 * The right column stays dormant (hidden, width 0) until T2/T5 give it content;
 * `setRightWidth()` is exposed for that future work.
 */
export interface LayoutShell {
  /** The grid root. Insert into `.app-shell` in place of the old `.content`. */
  root: HTMLElement
  /** Left column: the layer panel (`id="panel"`), fixed + width-resizable. */
  left: HTMLElement
  /** Center: the OpenLayers map host (`id="map"`). */
  map: HTMLElement
  /** Right column: dormant and hidden in T1; filled by T2 (Details) / T5 (Data Filter). */
  right: HTMLElement
  /** Bottom zone: full width; hosts the attribute-table drawer (height-resizable). */
  bottom: HTMLElement
  /** Register a callback fired after any size change (e.g. `map.updateSize()`). */
  onResize(cb: () => void): void
  /** Fire the registered resize callbacks. */
  notifyResize(): void
  /** Reveal + size the right column (T2/T5). Clamped and persisted. */
  setRightWidth(px: number): void
  /**
   * Reveal the right panel (T2 "Details"). On desktop the column expands to its
   * width; on mobile it becomes a full-screen overlay drawer, mutually exclusive
   * with the layer panel and the table.
   */
  openRight(): void
  /** Collapse/hide the right panel and re-fit the map. */
  closeRight(): void
  /**
   * Toggle the left panel's mobile drawer (≤820px). On mobile the panel, the
   * table and the right panel are mutually exclusive overlays, so opening the
   * panel closes the others. No-op affordance on desktop (the panel is always
   * visible there).
   */
  toggleLeftDrawer(): void
}

/** Below this width the side panels become slide-in overlays (matches styles.css). */
function isMobile(): boolean {
  return window.matchMedia('(max-width: 820px)').matches
}

const LS_KEYS = {
  left: 'gv.layout.leftWidth',
  right: 'gv.layout.rightWidth',
  table: 'gv.layout.tableHeight',
} as const

const BOUNDS = {
  left: { min: 220, max: 480, def: 300 },
  right: { min: 300, max: 560, def: 360 },
  table: { min: 120, def: 280 }, // upper bound is 60vh, computed at drag time
} as const

export function createLayoutShell(): LayoutShell {
  const root = el('div', 'layout-shell')

  const left = el('aside', 'panel layout-shell__left')
  left.id = 'panel'
  left.innerHTML = '<p class="panel-empty">Loading layers…</p>'

  const map = el('div', 'map layout-shell__map')
  map.id = 'map'
  map.setAttribute('role', 'application')
  map.setAttribute('aria-label', 'Map')

  const right = el('section', 'layout-shell__right')
  right.id = 'right-panel'
  right.hidden = true

  const bottom = el('div', 'layout-shell__bottom')
  // Pre-create the attribute drawer inside the bottom zone so
  // showAttributesDrawer() — which looks it up by `.attr-drawer` and reuses an
  // existing node — mounts it here in the grid instead of on document.body.
  // No change to attributesDrawer.ts is required.
  const drawer = el('div', 'attr-drawer')
  drawer.hidden = true
  bottom.appendChild(drawer)

  // Resize handles (absolutely positioned at the zone boundaries; the grid
  // tracks are driven by --left-w / --table-h CSS vars).
  const colHandle = el('div', 'layout-shell__handle layout-shell__handle--col')
  colHandle.setAttribute('role', 'separator')
  colHandle.setAttribute('aria-orientation', 'vertical')
  colHandle.setAttribute('aria-label', 'Resize layer panel')
  const rowHandle = el('div', 'layout-shell__handle layout-shell__handle--row')
  rowHandle.setAttribute('role', 'separator')
  rowHandle.setAttribute('aria-orientation', 'horizontal')
  rowHandle.setAttribute('aria-label', 'Resize table height')

  root.append(left, map, right, bottom, colHandle, rowHandle)

  const listeners: Array<() => void> = []
  const notifyResize = (): void => {
    for (const cb of listeners) cb()
  }

  // --- sizes -------------------------------------------------------------
  let leftW = readStored(LS_KEYS.left, BOUNDS.left.min, BOUNDS.left.max, BOUNDS.left.def)
  let tableH = readStored(LS_KEYS.table, BOUNDS.table.min, tableMax(), BOUNDS.table.def)
  // Right width is remembered but only applied when the panel is opened (it stays
  // collapsed to 0 until T2's "Details" reveals it).
  let rightW = readStored(LS_KEYS.right, BOUNDS.right.min, BOUNDS.right.max, BOUNDS.right.def)
  applyLeft(leftW)
  applyTable(tableH)

  function applyLeft(w: number): void {
    leftW = w
    root.style.setProperty('--left-w', `${w}px`)
  }
  function applyTable(h: number): void {
    tableH = h
    root.style.setProperty('--table-h', `${h}px`)
  }

  // --- drag handles ------------------------------------------------------
  makeDrag(colHandle, (x) => {
    applyLeft(clamp(x - root.getBoundingClientRect().left, BOUNDS.left.min, BOUNDS.left.max))
    notifyResize()
  }, () => writeStored(LS_KEYS.left, leftW))

  makeDrag(rowHandle, (_x, y) => {
    applyTable(clamp(root.getBoundingClientRect().bottom - y, BOUNDS.table.min, tableMax()))
    notifyResize()
  }, () => writeStored(LS_KEYS.table, tableH))

  // --- table open/close drives layout + updateSize -----------------------
  // The drawer toggles its own `hidden` attribute (show/hideAttributesDrawer).
  // Mirror that on the root so the handles show only when the table is open,
  // and re-run the resize callbacks so the map re-fits when the row appears.
  const syncTableOpen = (): void => {
    const open = !drawer.hidden
    root.classList.toggle('layout-shell--table-open', open)
    // Mobile: table, left drawer and right panel are mutually exclusive.
    if (open && isMobile()) {
      left.classList.remove('panel--open')
      closeRight()
    }
    notifyResize()
  }
  new MutationObserver(syncTableOpen).observe(drawer, {
    attributes: true,
    attributeFilter: ['hidden'],
  })

  // --- right panel (T2) --------------------------------------------------
  // Hoisted so syncTableOpen (above) can call closeRight for mobile exclusivity.
  function openRight(): void {
    right.hidden = false
    root.classList.add('layout-shell--right-open')
    if (isMobile()) {
      // Exclusive full-screen overlay: close the panel and table drawers.
      left.classList.remove('panel--open')
      drawer.hidden = true
    } else {
      root.style.setProperty('--right-w', `${rightW}px`)
    }
    notifyResize()
  }
  function closeRight(): void {
    right.hidden = true
    root.classList.remove('layout-shell--right-open')
    root.style.setProperty('--right-w', '0px')
    notifyResize()
  }

  return {
    root,
    left,
    map,
    right,
    bottom,
    onResize(cb) {
      listeners.push(cb)
    },
    notifyResize,
    setRightWidth(px) {
      rightW = clamp(px, BOUNDS.right.min, BOUNDS.right.max)
      root.style.setProperty('--right-w', `${rightW}px`)
      writeStored(LS_KEYS.right, rightW)
      notifyResize()
    },
    openRight,
    closeRight,
    toggleLeftDrawer() {
      const opening = !left.classList.contains('panel--open')
      if (opening && isMobile()) {
        drawer.hidden = true // close table (exclusive)
        closeRight() // close right panel (exclusive)
      }
      left.classList.toggle('panel--open', opening)
    },
  }
}

/** Drag helper: pointer-capture + rAF-batched moves (the "light debounce"). */
function makeDrag(
  handle: HTMLElement,
  onMove: (clientX: number, clientY: number) => void,
  onEnd: () => void,
): void {
  handle.addEventListener('pointerdown', (e: PointerEvent) => {
    e.preventDefault()
    handle.setPointerCapture(e.pointerId)
    document.body.classList.add('layout-shell-dragging')
    let lastX = e.clientX
    let lastY = e.clientY
    let frame = 0
    const move = (ev: PointerEvent): void => {
      lastX = ev.clientX
      lastY = ev.clientY
      if (!frame) {
        frame = requestAnimationFrame(() => {
          frame = 0
          onMove(lastX, lastY)
        })
      }
    }
    const up = (ev: PointerEvent): void => {
      handle.releasePointerCapture(ev.pointerId)
      handle.removeEventListener('pointermove', move)
      handle.removeEventListener('pointerup', up)
      document.body.classList.remove('layout-shell-dragging')
      if (frame) cancelAnimationFrame(frame)
      onEnd()
    }
    handle.addEventListener('pointermove', move)
    handle.addEventListener('pointerup', up)
  })
}

function tableMax(): number {
  return Math.round(window.innerHeight * 0.6)
}

/** Defensive localStorage read: bad/missing/unparsable → default, always clamped. */
function readStored(key: string, min: number, max: number, def: number): number {
  try {
    const raw = localStorage.getItem(key)
    if (raw == null) return def
    const n = Number(raw)
    return Number.isFinite(n) ? clamp(n, min, max) : def
  } catch {
    return def
  }
}

function writeStored(key: string, value: number): void {
  try {
    localStorage.setItem(key, String(Math.round(value)))
  } catch {
    /* storage unavailable (private mode / quota) — sizes just won't persist */
  }
}

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n))
}

function el(tag: string, className: string): HTMLElement {
  const node = document.createElement(tag)
  node.className = className
  return node
}
